// 2024 Find Steed (SRD 5.2.1): the Otherworldly Steed stat block, built for the slot level and the caster's spell
// attack bonus, save DC and Proficiency Bonus, in the monster-catalog shape that buildEnemyRecord normalizes.
// The steed is a Large ally that shares the caster's Initiative; casting again replaces it.
export const FIND_STEED_SPELL_ID = 'find-steed';

export const STEED_TYPES = Object.freeze({
  celestial: Object.freeze({ label: 'Celestial', damageType: 'radiant' }),
  fey: Object.freeze({ label: 'Fey', damageType: 'psychic' }),
  fiend: Object.freeze({ label: 'Fiend', damageType: 'necrotic' }),
});

const signed = (value) => `${value >= 0 ? '+' : '−'}${Math.abs(value)}`;

const bonusActionFor = (type, level, saveDc) => ({
  celestial: { name: 'Healing Touch (Celestial Only; Recharges after a Long Rest)',
    desc: `One creature within 5 feet of the steed regains a number of Hit Points equal to 2d8 + ${level}.` },
  fey: { name: 'Fey Step (Fey Only; Recharges after a Long Rest)',
    desc: 'The steed teleports, along with its rider, to an unoccupied space of your choice up to 60 feet away from itself.' },
  fiend: { name: 'Fell Glare (Fiend Only; Recharges after a Long Rest)',
    desc: `Wisdom Saving Throw: DC ${saveDc}, one creature within 60 feet the steed can see. Failure: The target has the Frightened condition until the end of your next turn.` },
}[type]);

/** The Otherworldly Steed for a cast at `spellLevel` (2+), as a monster detail. */
export const buildOtherworldlySteed = ({ spellLevel, creatureType, spellAttackBonus, spellSaveDc, proficiencyBonus }) => {
  const level = Math.max(2, Math.min(9, Math.floor(Number(spellLevel) || 2)));
  const type = STEED_TYPES[creatureType] ? creatureType : 'celestial';
  const { label, damageType } = STEED_TYPES[type];
  const attack = Number(spellAttackBonus) || 0;
  const damageDice = `1d8+${level}`;
  return {
    index: 'otherworldly-steed',
    name: 'Otherworldly Steed',
    size: 'Large',
    type: label.toLowerCase(),
    alignment: 'Neutral',
    armor_class: 10 + level,
    hit_points: 5 + 10 * level,
    hit_dice: `${level}d10`,
    speed: { walk: '60 ft.', ...(level >= 4 ? { fly: '60 ft.' } : {}) },
    strength: 18, dexterity: 12, constitution: 14, intelligence: 6, wisdom: 12, charisma: 8,
    senses: { passive_perception: 11 },
    languages: 'Telepathy 1 mile (works only with you)',
    challenge_rating: 0,
    xp: 0,
    proficiency_bonus: Number(proficiencyBonus) || 2,
    special_abilities: [{ name: 'Life Bond',
      desc: 'When you regain Hit Points from a level 1+ spell, the steed regains the same number of Hit Points if you’re within 5 feet of it.' }],
    actions: [{
      name: 'Otherworldly Slam',
      desc: `Melee Attack Roll: ${signed(attack)}, reach 5 ft. Hit: 1d8 + ${level} ${label === 'Celestial' ? 'Radiant' : label === 'Fey' ? 'Psychic' : 'Necrotic'} damage.`,
      attack_bonus: attack,
      damage: [{ damage_dice: damageDice, damage_type: { name: damageType } }],
      damage_dice: damageDice,
      damage_type: { name: damageType },
    }],
    bonus_actions: [bonusActionFor(type, level, Number(spellSaveDc) || 10)],
  };
};

/**
 * Where to place a Large steed next to the caster's token: the first of eight spots around it (two squares away, so the
 * 2x2 footprint stays clear of the caster) that is on the map and not over another token. Positions are normalized.
 */
export const steedPlacementCandidates = ({ ownerToken, columns, rows }) => {
  if (!ownerToken || !(columns > 0) || !(rows > 0)) return [];
  const dx = 2 / columns, dy = 2 / rows;
  return [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]
    .map(([x, y]) => ({ x: Number(ownerToken.x) + x * dx, y: Number(ownerToken.y) + y * dy }))
    .filter((point) => point.x > dx / 2 && point.x < 1 - dx / 2 && point.y > dy / 2 && point.y < 1 - dy / 2);
};
